import { useRef, useState, useContext } from "react";
import EventMap from "../components/EventMap";

import EventPeople from "components/EventPeople";
import EventSources from "components/EventSources";

import ActiveLocationContext from "hooks/active-location-context";
import InfoBoxContext from "hooks/info-box-context";

import IconFlag from "assets/svgs/flag.svg";
import Pin from "assets/svgs/pin.svg";

import useAnalytics from "hooks/useAnalytics";
import useOnScreen from "hooks/useOnScreen";

const filterRecordsById = (arr1, arr2) =>
  arr1.filter((record) => arr2.includes(record.item_id));

// Search for this and replace with helper function
const findById = (id, items) => {
  return items.find((item) => item.item_id === id);
};

const EventTime = ({ time, apprxTime = null }) => {
  const tmp = time ? time : apprxTime;
  return (
    <>
      {tmp ? (
        <strong className="flex-shrink-0 font-semibold">{tmp}:</strong>
      ) : null}
    </>
  );
};

export default function Events({
  caseLocationRecords,
  casePeopleRecords,
  caseSignificantEventRecords,
  caseSourcesRecords,
}) {
  const [activeTimelineEvent, setActiveTimelineEvent] = useState(
    caseSignificantEventRecords[0] && caseSignificantEventRecords[0].item_id
  );

  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef();
  const isVisible = useOnScreen(mapRef);

  const { setActiveLocationId } = useContext(
    ActiveLocationContext
  );

  const { setShowInfoBox } = useContext(InfoBoxContext);

  const { track } = useAnalytics();

  const handleEventClick = (event) => {
    setActiveTimelineEvent(event.item_id);
    event.location ? setShowInfoBox(true) : setShowInfoBox(false);
    setActiveLocationId(event.location);
  };

  const activeEventId = caseSignificantEventRecords.filter(
    (event) => event.item_id === activeTimelineEvent
  );

  return (
    <section className="relative px-4 timeline-wrapper lg:flex lg:min">
      <div className="relative overflow-hidden py-8 z-10 lg:absolute lg:-inset-y-0 lg:left-0 lg:overflow-y-scroll lg:px-4 lg:shadow-xl lg:w-1/2">
        <header className="flex items-center justify-between">
          <h1 className="highlight mb-8">Timeline</h1>
        </header>
        <div className="relative timeline-container">
          {caseSignificantEventRecords.map((event, index) => {
            const {
              item_id,
              date,
              description_text,
              location,
              persons,
              sources,
              time,
              time_approximate,
              summary_text,
            } = event;

            const isLastItem = index + 1 === caseSignificantEventRecords.length;
            const isActive = item_id === activeTimelineEvent;
            const eventLocation = findById(location, caseLocationRecords);
            const eventSources = filterRecordsById(caseSourcesRecords, sources);
            const eventPersons = filterRecordsById(casePeopleRecords, persons);

            return (
              <button
                onClick={() => {
                  handleEventClick(event);
                }}
                className={`block relative cursor-pointer mb-8 select-text text-left timeline-item ${
                  !isLastItem ?? "pb-8"
                }`}
                key={item_id}
              >
                <h3 className="flex items-center relative text-xl font-semibold pb-2 z-20">
                  <span
                    className={`w-4 h-4 bg-white mr-2 border-3 rounded-full timeline-circle ${
                      isActive && "timeline-active"
                    }`}
                  ></span>
                  {date && date}
                </h3>
                <p className="flex relative ml-7 text-sm z-20">
                  {time || time_approximate ? (
                    <>
                      <EventTime time={time} apprxTime={time_approximate} />
                      &nbsp;
                    </>
                  ) : null}
                  <span className="flex items-center">
                    {summary_text}{" "}
                    {location && (
                      <i
                        aria-hidden="true"
                        className={`ml-2 ${isActive && "text-red"}`}
                      >
                        <Pin width="10" />
                      </i>
                    )}
                  </span>
                </p>
                <details className="ml-7 mt-4 open" open={true}>
                  <summary
                    className="text-gray-500 text-xs md:hidden"
                    data-open="Hide"
                    data-close="Show"
                  >
                    {" "}
                    Details
                  </summary>
                  <div className="bg-gray-50 mt-4 p-4">
                    {description_text && (
                      <p className="mb-2 text-sm">{description_text}</p>
                    )}
                    {eventLocation && (
                      <p
                        key={eventLocation.summary_text}
                        className="flex font-semibold mb-2 mt-4 text-sm text-red"
                      >
                        <i aria-hidden="true" className="mr-2">
                          <Pin width="14" />
                        </i>{" "}
                        {eventLocation.summary_text}
                      </p>
                    )}
                    {eventPersons &&
                      eventPersons?.length !== undefined &&
                      eventPersons.length > 0 && (
                        <EventPeople relatedPeople={eventPersons} />
                      )}
                    {eventSources &&
                      eventSources?.length !== undefined &&
                      eventSources.length > 0 && (
                        <EventSources relatedSources={eventSources} />
                      )}
                    <div className="flex justify-end pt-4">
                      <a
                        className="duration-300 flex font-semibold text-gray-400 text-sm transition-color hover:text-gray-500"
                        href="https://uncovered.com/flag-an-event"
                      >
                        Flag this event <IconFlag className="ml-2" />
                      </a>
                    </div>
                  </div>
                </details>
              </button>
            );
          })}
        </div>
      </div>
      <div ref={mapRef} className="relative mt-8 md:mt-0 lg:absolute lg:-inset-y-0 lg:left-1/2 lg:overflow-scroll lg:w-1/2">
        { (isVisible || mapVisible)  ? (<EventMap
          activeTimelineEventLocationId={
            (activeEventId[0] && activeEventId[0].location) || "1"
          }
          reopenEventInfoBox={true}
          fullLocations={caseLocationRecords}
        />) 
        :
        (
          <div 
            className="flex items-center justify-center h-full bg:white bg-opacity-50"
            onClick={() => {
              setMapVisible(true);
              track("select_content", {
                content_type: "map",
                content_id: "timeline",
              });
            }}
          >
            <h3 className="hidden lg:block text-black text-2xl">Click to Activate the Map</h3>
            <h3 className="block lg:hidden text-black text-2xl">Tap to Activate the Map</h3>
          </div>
        )}

      </div>
    </section>
  );
}
